import { useCallback } from 'react'
import { isObjectEligibleForSelectAll } from '@/components/app/editor/select-all-policy'
import {
  applySelectionLockPolicy,
  areIdArraysEqual,
  isShapeSelectable,
  normalizeSelectableIdsForScope,
} from '../helpers/selection-scope'
import type { ShapeSelectionHistoryMeta } from '@/lib/shapes/types/shape-selection-history-meta'
import { isObjectSelectionAddRejected } from '@/components/app/editor/selection-chrome/geometry-blocked-feedback'
import { getShapeById } from '../helpers/shape-list'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { SelectionHistoryApi } from './use-selection-history'
import type { EditScopeRefsApi } from './use-edit-scope-refs'
import type { FeedbackPulsesApi } from './use-feedback-pulses'

export function useShapeSelection(
  ctx: ShapeManagerStateApi & SelectionHistoryApi & EditScopeRefsApi & FeedbackPulsesApi
) {
  const {
    editScopeGroupIdRef,
    onShapeClick,
    recordShapeSelectionHistory,
    selectedShapeIdsRef,
    setSelectedShapeIds,
    shapesRef,
    triggerFrozenObjectOutlinePulse,
    triggerSelectionChromeBlockedPulse,
  } = ctx

  const clearSelection = useCallback(
    (opts?: { recordHistory?: boolean; historyTxnId?: string }) => {
      if (opts?.recordHistory) {
        setSelectedShapeIds((prev) => {
          const next: string[] = []

          selectedShapeIdsRef.current = next
          recordShapeSelectionHistory(prev, next, { historyTxnId: opts?.historyTxnId })
          return next
        })
      } else {
        setSelectedShapeIds([])
      }
    },
    [recordShapeSelectionHistory, selectedShapeIdsRef, setSelectedShapeIds]
  )

  const selectAll = useCallback(() => {
    const before = [...selectedShapeIdsRef.current]
    const next = shapesRef.current
      .filter(
        (s) => isShapeSelectable(s, editScopeGroupIdRef.current) && isObjectEligibleForSelectAll(s)
      )
      .map((s) => s.id)

    selectedShapeIdsRef.current = next
    setSelectedShapeIds(next)
    recordShapeSelectionHistory(before, next)
  }, [
    recordShapeSelectionHistory,
    editScopeGroupIdRef,
    selectedShapeIdsRef,
    setSelectedShapeIds,
    shapesRef,
  ])

  const selectNone = useCallback(() => {
    const before = [...selectedShapeIdsRef.current]
    const next: string[] = []

    selectedShapeIdsRef.current = next
    setSelectedShapeIds(next)
    recordShapeSelectionHistory(before, next)
  }, [recordShapeSelectionHistory, selectedShapeIdsRef, setSelectedShapeIds])

  const selectShapes = useCallback(
    (ids: string[]) => {
      const next = applySelectionLockPolicy(
        normalizeSelectableIdsForScope(ids, shapesRef.current, editScopeGroupIdRef.current),
        shapesRef.current
      )

      selectedShapeIdsRef.current = next
      setSelectedShapeIds(next)
    },
    [editScopeGroupIdRef, selectedShapeIdsRef, setSelectedShapeIds, shapesRef]
  )

  const selectShapesForHistory = useCallback(
    (ids: string[]) => {
      const next = applySelectionLockPolicy(
        normalizeSelectableIdsForScope(ids, shapesRef.current, editScopeGroupIdRef.current),
        shapesRef.current,
        { preserveAllLockedMultiSelection: true }
      )

      selectedShapeIdsRef.current = next
      setSelectedShapeIds(next)
    },
    [editScopeGroupIdRef, selectedShapeIdsRef, setSelectedShapeIds, shapesRef]
  )

  const selectShapesWithHistory = useCallback(
    (ids: string[], meta?: ShapeSelectionHistoryMeta) => {
      const before = [...selectedShapeIdsRef.current]
      const next = applySelectionLockPolicy(
        normalizeSelectableIdsForScope(ids, shapesRef.current, editScopeGroupIdRef.current),
        shapesRef.current
      )

      selectedShapeIdsRef.current = next
      setSelectedShapeIds(next)
      recordShapeSelectionHistory(before, next, meta)
    },
    [
      recordShapeSelectionHistory,
      editScopeGroupIdRef,
      selectedShapeIdsRef,
      setSelectedShapeIds,
      shapesRef,
    ]
  )

  const getSelectedShapeIds = useCallback(() => {
    return selectedShapeIdsRef.current
  }, [selectedShapeIdsRef])

  const toggleSelection = useCallback(
    (shapeId: string) => {
      let selectionAddRejected = false

      setSelectedShapeIds((prev) => {
        const shape = getShapeById(shapesRef.current, shapeId)

        if (!shape) return prev
        const hasLockedSelected = prev.some(
          (id) => getShapeById(shapesRef.current, id)?.locked === true
        )
        const hasFrozenSelected = prev.some(
          (id) => getShapeById(shapesRef.current, id)?.frozen === true
        )
        const next = (() => {
          if (prev.includes(shapeId)) {
            return prev.filter((id) => id !== shapeId)
          }

          if (shape.locked === true) {
            if (prev.length > 0) return prev
            return [shapeId]
          }

          if (shape.frozen === true) {
            if (prev.length > 0) return prev
            return [shapeId]
          }

          if (hasLockedSelected) return prev

          if (hasFrozenSelected) return prev
          return [...prev, shapeId]
        })()

        if (areIdArraysEqual(prev, next)) {
          if (
            isObjectSelectionAddRejected({
              previousIds: prev,
              incomingId: shapeId,
              incomingLocked: shape.locked === true,
              incomingFrozen: shape.frozen === true,
              previousIncludesLocked: hasLockedSelected,
              previousIncludesFrozen: hasFrozenSelected,
            })
          ) {
            selectionAddRejected = true
          }

          return prev
        }

        selectedShapeIdsRef.current = next
        recordShapeSelectionHistory(prev, next)
        return next
      })

      // Also catch the case where a frozen shape was rejected because text boxes are selected
      // (prev was empty so isObjectSelectionAddRejected wouldn't have fired above).
      if (!selectionAddRejected) {
        const shape = getShapeById(shapesRef.current, shapeId)

        if (shape?.frozen === true && !selectedShapeIdsRef.current.includes(shapeId)) {
          selectionAddRejected = true
        }
      }

      if (selectionAddRejected) {
        const shape = getShapeById(shapesRef.current, shapeId)

        if (shape?.frozen === true) {
          triggerFrozenObjectOutlinePulse(shapeId)
        } else {
          triggerSelectionChromeBlockedPulse()
        }
      }

      if (!selectionAddRejected) {
        onShapeClick?.(shapeId, true)
      }
    },
    [
      onShapeClick,
      recordShapeSelectionHistory,
      triggerFrozenObjectOutlinePulse,
      triggerSelectionChromeBlockedPulse,
      selectedShapeIdsRef,
      setSelectedShapeIds,
      shapesRef,
    ]
  )

  return {
    clearSelection,
    selectAll,
    selectNone,
    selectShapes,
    selectShapesForHistory,
    selectShapesWithHistory,
    getSelectedShapeIds,
    toggleSelection,
  }
}

export type ShapeSelectionApi = ReturnType<typeof useShapeSelection>
